import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Gift } from 'lucide-react';
import { cn, Paginator } from '@jeraisonne/ui';
import { useAuth } from '@/lib/auth';
import {
  filterSkillcards,
  recentSkillcards,
  skillcardStats,
  useStudentSkillcards,
  type SkillcardFilter,
  type StudentSkillcard,
} from '@/lib/cards/student-skillcards';
import { SkillcardList } from '@/features/skillcards/skillcard-row';
import { usePagedList } from '@/lib/use-paged-list';
import { filterCards, type CardFilterable, type CardFilters } from '@jeraisonne/db/cards';
import { SkillcardFilters } from '@/features/skillcards/skillcard-filters';
import { deckStatus, formatDeckDate } from '@jeraisonne/db/cards';
import { useMyDeck } from '@/lib/skillcards/purchase';
import { GiftCodeForm } from '@/features/gifts/redeem-gift-page';
import { SkillcardExplainer } from '@/features/skillcards/skillcard-explainer';
import { AddSkillcardsButton } from '@/features/skillcards/skillcard-browser';

const FILTERS: { id: SkillcardFilter; label: string }[] = [
  { id: 'all', label: 'Toutes' },
  { id: 'to_work', label: 'À travailler' },
  { id: 'to_rework', label: 'À retravailler' },
  { id: 'mastered', label: 'Maîtrisées' },
];

/**
 * Route élève `/student/skillcards` — « Mes SKILLCARDS » : statistiques
 * (cartes, XP, maîtrise moyenne, maîtrisées, à travailler) et toutes les
 * cartes, filtrables. Chaque carte ouvre sa page classique `/card/$cardId`.
 */
function toStudentCardFilterable(c: StudentSkillcard): CardFilterable {
  return { title: c.title, levels: c.levels, domainLabel: c.domain_label };
}

export function StudentSkillcardsPage({
  dashboardPath = '/student/dashboard',
}: {
  /** Retour : tableau de bord de l'espace courant (élève ou joueur). */
  dashboardPath?: '/student/dashboard' | '/player/dashboard';
} = {}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const cards = useStudentSkillcards(user?.id);
  // Deck acheté (migration 0304) : une date de fin commune à toutes les cartes.
  const deck = useMyDeck(user?.id);
  const deckInfo = deckStatus(deck.data);
  const [filter, setFilter] = useState<SkillcardFilter>('all');
  const list = cards.data ?? [];
  const stats = skillcardStats(list);
  const [search, setSearch] = useState<CardFilters>({});
  const shown = filterCards(
    // « À retravailler » garde son ordre : celles qui ont le plus perdu d'abord.
    filter === 'to_rework'
      ? filterSkillcards(list, filter)
      : recentSkillcards(filterSkillcards(list, filter), list.length),
    toStudentCardFilterable,
    search,
  );
  const { items: pageCards, paginator } = usePagedList(shown, {
    resetKeys: [filter, search.q, search.level, search.domain],
    unit: 'cartes',
    label: 'Pages des SKILLCARDS',
  });

  // Effectifs par onglet (remplacent les anciennes tuiles qui doublaient les filtres).
  const counts: Record<SkillcardFilter, number> = {
    all: list.length,
    to_work: stats.toWork,
    to_rework: filterSkillcards(list, 'to_rework').length,
    mastered: stats.mastered,
  };
  const isPlayer = dashboardPath === '/player/dashboard';

  return (
    <div className="mx-auto max-w-3xl space-y-5 px-4 py-6 pb-[max(env(safe-area-inset-bottom),1.5rem)] sm:py-8">
      <div>
        <Link
          to={dashboardPath}
          className="text-ink-500 hover:text-ink-800 inline-flex min-h-[2.75rem] items-center gap-1.5 text-sm"
        >
          <ArrowLeft className="h-4 w-4" /> Accueil
        </Link>
        <h1 className="font-display text-ink-900 text-2xl font-extrabold">Mes SKILLCARDS</h1>
        {list.length > 0 && (
          <p className="text-ink-500 flex flex-wrap gap-x-1.5 text-sm">
            <span className="whitespace-nowrap">
              {stats.count} carte{stats.count > 1 ? 's' : ''}
            </span>
            <span className="whitespace-nowrap">
              · {stats.totalXp.toLocaleString('fr-FR')} HORA
            </span>
            {stats.averageMastery !== null && (
              <span className="whitespace-nowrap">· maîtrise moyenne {stats.averageMastery} %</span>
            )}
          </p>
        )}
        {deck.data && (
          <p className={cn('mt-1 text-sm', deckInfo.active ? 'text-success-700' : 'text-ink-600')}>
            {deckInfo.active ? (
              <>
                Cartes achetées actives jusqu’au <strong>{formatDeckDate(deck.data)}</strong>{' '}
                (chaque achat prolonge d’un an).
              </>
            ) : (
              <>
                Tes cartes achetées ont expiré le {formatDeckDate(deck.data)} : un nouvel achat les
                réactive toutes pour un an.
              </>
            )}
          </p>
        )}
      </div>

      <SkillcardExplainer defaultOpen={!cards.isLoading && list.length === 0} />

      {cards.error && (
        <p role="alert" className="text-accent-600 text-sm">
          Impossible de charger tes SKILLCARDS. Réessaie dans un instant.
        </p>
      )}

      {list.length > 0 && (
        <>
          <div
            role="tablist"
            aria-label="Filtrer"
            className="bg-ink-100 grid grid-cols-2 gap-1 rounded-xl p-1 sm:grid-cols-4"
          >
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                role="tab"
                aria-selected={filter === f.id}
                onClick={() => setFilter(f.id)}
                className={cn(
                  'flex min-h-[2.75rem] items-center justify-center gap-1.5 rounded-lg text-sm font-semibold transition-colors active:scale-[0.98]',
                  filter === f.id ? 'text-ink-900 bg-white shadow-sm' : 'text-ink-500',
                )}
              >
                {f.label}
                <span
                  aria-hidden
                  className={cn(
                    'min-w-[1.25rem] rounded-full px-1.5 text-xs',
                    f.id === 'to_work' && counts.to_work > 0
                      ? 'bg-amber-100 text-amber-800'
                      : f.id === 'to_rework' && counts.to_rework > 0
                        ? 'bg-red-100 text-red-700'
                        : 'bg-ink-200/70 text-ink-600',
                  )}
                >
                  {counts[f.id]}
                </span>
              </button>
            ))}
          </div>

          <SkillcardFilters
            cards={list.map(toStudentCardFilterable)}
            value={search}
            onChange={setSearch}
          />
        </>
      )}

      {cards.isLoading ? (
        <p className="text-ink-500 text-sm">Chargement…</p>
      ) : (
        <>
          <SkillcardList
            cards={pageCards}
            showAssigner
            empty={
              filter === 'to_work'
                ? 'Rien à travailler pour le moment.'
                : filter === 'to_rework'
                  ? 'Aucune carte n’a perdu d’HORA : bravo, continue d’être régulier !'
                  : filter === 'mastered'
                    ? 'Aucune carte maîtrisée pour l’instant : continue !'
                    : search.q || search.level || search.domain
                      ? 'Aucune carte ne correspond à ta recherche.'
                      : isPlayer
                        ? 'Pas encore de SKILLCARD. Elles apparaissent dès que tu joues un exercice relié à une carte (JeJoue) ou que tu en débloques une.'
                        : 'Pas encore de SKILLCARD. Elles apparaissent quand tu travailles un exercice relié à une carte, que tu en achètes une ou quand ton prof t’en ajoute une.'
            }
          />
          <Paginator {...paginator} />
        </>
      )}

      {/* Secondaire : obtenir des cartes, code cadeau. */}
      <div className="space-y-2">
        <AddSkillcardsButton userId={user?.id ?? null} />
        {/* Carte cadeau (migration 0326) : saisir un code reçu. */}
        <details className="border-ink-100 ocean:bg-white/5 rounded-2xl border bg-white px-4">
          <summary className="text-ink-900 flex min-h-[2.75rem] cursor-pointer items-center gap-2 text-sm font-semibold">
            <Gift className="text-accent-500 h-5 w-5" aria-hidden /> J’ai un code cadeau
          </summary>
          <div className="space-y-2 pb-4">
            <GiftCodeForm
              onSubmitCode={(code) => void navigate({ to: '/cadeau/$code', params: { code } })}
            />
            <Link
              to="/cadeaux"
              className="text-primary-600 inline-flex min-h-[2.75rem] items-center text-sm font-semibold"
            >
              Mes cadeaux offerts
            </Link>
          </div>
        </details>
      </div>
    </div>
  );
}
